import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
  upsertOrGroup,
} from "@/shared/contracts/query-helpers";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";

type DepartmentItem = { id: string; name: string };

export function EmployeeFilter() {
  const nav = useNavigate({ from: "/app/assign-position" });
  const search = useSearch({
    from: "/app/assign-position",
  }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const nameFilter = findCondition(filters, "name");
  const emailFilter = findCondition(filters, "email");
  const departmentFilter = findCondition(filters, "currentDepartmentId");
  const unassignedFilter = findCondition(filters, "currentPositionId");

  const [searchValue, setSearchValue] = useState<string>(
    (nameFilter?.value as string) || (emailFilter?.value as string) || "",
  );

  useEffect(() => {
    const current =
      (nameFilter?.value as string) || (emailFilter?.value as string) || "";
    setSearchValue(current);
  }, [nameFilter?.value, emailFilter?.value]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      // Use OR group so search matches either name OR email OR id
      nextFilters = upsertOrGroup(filters, [
        { field: "name", op: "contains", value: val },
        { field: "email", op: "contains", value: val },
        { field: "id", op: "contains", value: val },
      ]);
    } else {
      // Remove all search-related filters
      nextFilters = removeConditions(filters, "name");
      nextFilters = removeConditions(nextFilters, "email");
      nextFilters = removeConditions(nextFilters, "id");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const departmentValue = (departmentFilter?.value as string) || "";
  const unassignedValue = unassignedFilter?.op === "isNull" ? "unassigned" : "";

  const hasFilter =
    !!nameFilter?.value ||
    !!emailFilter?.value ||
    !!departmentValue ||
    !!unassignedValue;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາພະນັກງານ..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <InfiniteCombobox<DepartmentItem>
            queryKey={["departments", "filter"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={departmentValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "currentDepartmentId",
                  op: "eq",
                  value: val,
                });
              } else {
                nextFilters = removeConditions(filters, "currentDepartmentId");
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ເລືອກພະແນກ"
            className="h-8 w-full sm:w-40"
            clearable
          />
          <Button
            variant={unassignedValue ? "default" : "outline"}
            size="sm"
            onClick={() => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (unassignedValue) {
                nextFilters = removeConditions(filters, "currentPositionId");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "currentPositionId",
                  op: "isNull",
                  value: undefined,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            className="h-8 whitespace-nowrap"
          >
            ບໍ່ມີຕໍາແໜ່ງ
          </Button>
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({ search: { ...search, filters: undefined, offset: 0 } })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
